import { CommonResourceKey } from '@shared/models/ResourceKeys';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import CoreButton from '../core/button/CoreButton';
import { IconName } from '../core/images/IconMap';
import { getUpgradeEntryPath } from './billing/upgradeNavigation';

interface IOrgLinksProps {
    variant:
        | 'orgDetails'
        | 'orgBilling'
        | 'orgInvites'
        | 'orgMembers'
        | 'orgMe'
        | 'orgAuthClient'
        | 'orgMetrics'
        | 'orgProjects'
        | 'orgSettings'
        | 'orgUpgradeRedirect'
        | 'orgLicenseKeys'
        | 'orgManagedOrgs'
        | 'orgProfiles'
        | 'orgProduct'
        | 'orgGovernance';
    organizationId: string;
    textOverride?: string;
}

export default function OrgLinks({ variant, organizationId, textOverride }: IOrgLinksProps) {
    const { t } = useTranslation();
    const { organizationId: parentOrgId, managedOrgId } = useParams();
    let url = '';
    let textKey: CommonResourceKey = 'common:other.title';
    let endIcon: IconName | undefined;
    let appendManagedOrg = false;
    let destinationPath: string | null = null;

    if (!variant || !organizationId) return null;

    // handle for reseller managed org navigation
    if (organizationId !== parentOrgId && parentOrgId && organizationId === managedOrgId) {
        organizationId = parentOrgId;
        appendManagedOrg = true;
    }

    switch (variant) {
        case 'orgMembers':
            url = `/members`;
            textKey = 'common:organization.canViewMembers.title';
            break;
        case 'orgBilling':
            url = `/billing`;
            textKey = 'common:organization.canManageBilling.title';
            break;
        case 'orgDetails':
            url = `/`;
            textKey = 'common:organization.titleLong';
            break;
        case 'orgInvites':
            url = `/members/invites`;
            textKey = 'common:organization.invite.view';
            break;
        case 'orgMe':
            url = `/`;
            textKey = 'common:organization.member.me';
            break;
        case 'orgAuthClient':
            url = `/projects`;
            textKey = 'common:organization.canCreateAuthClients.title';
            break;
        case 'orgMetrics':
            url = `/metrics`;
            textKey = 'common:organization.metrics.view';
            break;
        case 'orgProjects':
            url = `/projects`;
            textKey = 'common:organization.projects.view';
            break;
        case 'orgUpgradeRedirect':
            destinationPath = getUpgradeEntryPath({
                organizationId,
                managedOrgId: appendManagedOrg ? managedOrgId : undefined,
            });
            textKey = 'common:billing.view';
            endIcon = 'externalLink';
            break;
        case 'orgSettings':
            url = `/settings`;
            textKey = 'common:settings.title';
            break;
        case 'orgLicenseKeys':
            url = `/licensekey`;
            textKey = 'common:license.key.titlePlural';
            break;
        case 'orgManagedOrgs':
            url = `/managed`;
            textKey = 'common:organization.managed.title';
            break;
        case 'orgProduct':
            url = `/purchase`;
            textKey = 'common:license.key.product.title';
            break;
        case 'orgProfiles':
            url = `/profiles`;
            textKey = 'common:organization.profiles.title';
            break;
        case 'orgGovernance':
            url = `/governance`;
            textKey = 'common:organization.governance.title';
            break;
        default:
            return null;
    }

    return (
        <CoreButton
            $color="subsection"
            to={
                destinationPath ??
                (appendManagedOrg
                    ? `/org/${organizationId}/managed/${managedOrgId}` + url
                    : `/org/${organizationId}` + url)
            }
            viewTransition
            endIcon={endIcon}>
            {textOverride ?? t(textKey)}
        </CoreButton>
    );
}
